
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { FormGroup, Label } from "reactstrap";
import DropIcon from "../../assets/images/375_vector.svg";
import Cancel from "../../assets/images/cancel.svg";

interface FilterInputProps {
  placeholder: string;
  label?: string;
  dropdown: boolean;
  setDropdown: (state: boolean) => void;
  formDataValue?: string;
  valueName: string;
  inputArray: string[];
  change: (e: React.ChangeEvent<HTMLInputElement>) => void;
  dropdownRef: React.RefObject<HTMLDivElement | null>;
  clearable: boolean;
  isRequired: boolean;

  /** Nested sub-dropdown props */
  dependentForValue?: string;
  dependentLabel?: string;
  dependentName?: string;
  dependentOptions?: string[];
  dependentValue?: string | null;
  isDependentOpen?: boolean;
  setDependentOpen?: (open: boolean) => void;
  onDependentChange?: (value: string | null) => void;
}

const FilterInput: React.FC<FilterInputProps> = ({
  placeholder,
  label,
  dropdown,
  setDropdown,
  formDataValue,
  valueName,
  inputArray,
  change,
  dropdownRef,
  clearable,
  isRequired,

  dependentForValue,
  dependentLabel,
  dependentName = "claimsCategory",
  dependentOptions,
  dependentValue,
  isDependentOpen,
  setDependentOpen,
  onDependentChange,
}) => {

  const displayValue =
    formDataValue === dependentForValue && dependentValue
      ? `${dependentValue}`
      : formDataValue || placeholder;


  const dependentRowRef = useRef<HTMLDivElement | null>(null);


  const [panelPos, setPanelPos] = useState<{ top: number; left: number } | null>(
    null
  );


  const [flipLeft, setFlipLeft] = useState(false);


  const isOptionWithDependent = (option: string) =>
    option === dependentForValue && (dependentOptions?.length || 0) > 0;


  const handleOptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    change(e);

    const selected = e.target.value;
    const hasDependent = isOptionWithDependent(selected);

    if (hasDependent) {
      // Open the sub-menu and compute its position beside the row
      openSubDropdownBesideRow();
    } else {
      // Close both for normal behavior
      setDependentOpen?.(false);
      setDropdown(false);
    }
  };

  const toggleMainDropdown = () => setDropdown(!dropdown);


  const clearMainSelection = (e: React.MouseEvent<HTMLImageElement>) => {
    e.stopPropagation();
    handleOptionChange({
      target: { name: valueName, value: "", type: "radio" } as any,
    } as React.ChangeEvent<HTMLInputElement>);
    onDependentChange?.(null);
  };


  const toggleDependent = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation();
    if (!isDependentOpen) {
      openSubDropdownBesideRow();
    } else {
      setDependentOpen?.(false);
    }
  };

  const openSubDropdownBesideRow = () => {
    setDependentOpen?.(true);
    requestAnimationFrame(() => {
      const rowEl = dependentRowRef.current;
      if (!rowEl) return;
      const rect = rowEl.getBoundingClientRect();

      // Default: float to the right of the row (8px gap)
      let left = rect.right + 8;
      const top = rect.top; // align tops

      // Flip if would overflow off-screen
      const menuWidth = 240; // approx, adjust if needed
      const willOverflowRight = left + menuWidth > window.innerWidth - 8;
      setFlipLeft(willOverflowRight);
      if (willOverflowRight) {
        left = rect.left - 8 - menuWidth; // float to the left of the row
      }

      setPanelPos({ top, left });
    });
  };

  useEffect(() => {
    const onDocClick = (ev: MouseEvent) => {
      const mainEl = dropdownRef?.current;
      const clickedInsideMain = !!mainEl && mainEl.contains(ev.target as Node);

      // Since the floating panel is rendered in a portal, detect by class name
      const targetEl = ev.target as HTMLElement;
      const clickedInsidePanel = !!targetEl.closest(".floating-sub-dropdown");

      if (!clickedInsideMain && !clickedInsidePanel) {
        setDependentOpen?.(false);
        // Don't forcibly close the main dropdown; let UX decide
        // setDropdown(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [dropdownRef, setDependentOpen]);


  useEffect(() => {
    if (!dropdown) {
      setDependentOpen?.(false);
    }
  }, [dropdown, setDependentOpen]);


  const handleDependentRadio = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value || null;
    onDependentChange?.(val);
    // Close both after selection (you can keep main open if desired)
    setDependentOpen?.(false);
    setDropdown(false);
  };


  //  Helper to select a category from row click
  const handleDependentSelect = (val: string) => {
    onDependentChange?.(val || null);
    setDependentOpen?.(false);
    setDropdown(false);
  };



  const renderFloatingPanel = () => {
    if (!isDependentOpen || !panelPos) return null;

    return ReactDOM.createPortal(
      <div
        className="floating-sub-dropdown"
        style={{
          position: "fixed",
          top: `${panelPos.top}px`,
          left: `${panelPos.left}px`,
          minWidth: 240,
          background: "#2b2b2b",
          border: "1px solid #333",
          borderRadius: 6,
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
          padding: "8px 10px",
          zIndex: 10000,
          color: "#fff"
        }}
      >
        {dependentLabel && (
          <div
            style={{
              fontSize: "0.85rem",
              color: "#fff",
              marginBottom: 6,
            }}
          >
            {dependentLabel}
          </div>
        )}

        {(dependentOptions || []).map((sub) => (
          <label
            key={sub}

            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 0",
              color: "#fff"
            }}

            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}

          >
            <input
              type="radio"
              name={dependentName}
              value={sub}
              checked={dependentValue === sub}
              onChange={handleDependentRadio}
            />
            <span style={{ fontSize: "0.95rem", color: "#fff" }}>{sub}</span>
          </label>
        ))}

        {(!dependentOptions || dependentOptions.length === 0) && (
          <div style={{ color: "#020c14ff" }}>
            {dependentLabel || "No options"}
          </div>
        )}
      </div>,
      document.body
    );
  };

  return (
    <FormGroup className="formgroup">
      {label && (
        <Label className="custom-label">
          {label}
          {isRequired && <span className="required-asterisk">*</span>}
        </Label>
      )}

      <div
        ref={dropdownRef}
        className={`custom-dropdown ${dropdown ? "active" : ""}`}
      >
        {/* Main button */}
        <div className="dropdown-button" onClick={toggleMainDropdown}>
          {displayValue}
          <div className="dropdown-button-child">
            {clearable && (
              <img
                src={Cancel}
                alt="Clear"
                className="clear-drop-down"
                onClick={clearMainSelection}
              />
            )}
            <img src={DropIcon} alt="Dropdown Icon" className="dropdown-icon" />
          </div>
        </div>
       
        {/* Main dropdown content */}
        {dropdown && (
          <div className="dropdown-content" style={{ overflow: "visible" }}>
            {inputArray.length > 0 ? (
              inputArray.map((option) => {
                const isDependent = isOptionWithDependent(option);
                return (
                  <div
                    key={option}
                    className="dropdown-row"
                    // Capture the row element ONLY for the dependent option
                    ref={isDependent ? dependentRowRef : null}
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                    }}
                  >
                    <label className="dropdown-label">
                      <input
                        type="radio"
                        name={valueName}
                        value={option}
                        checked={formDataValue === option}
                        onChange={handleOptionChange}
                      />
                      <span className="dropdown-text">{option}</span>
                    </label>

                    {/* Only show chevron for the dependent option */}
                    {isDependent && (
                      <span
                        className={`sub-dropdown-toggle ${isDependentOpen ? "open" : ""
                          }`}
                        title={dependentLabel || "Select"}
                        onClick={toggleDependent}
                        style={{
                          cursor: "pointer",
                          padding: 4,
                          borderRadius: 4,
                          display: "inline-flex",
                          alignItems: "center",
                        }}
                      >
                        <img
                          src={DropIcon}
                          alt="Sub Dropdown Icon"
                          className="dropdown-icon"
                        />
                      </span>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="no-results">{placeholder}</div>
            )}
          </div>
        )}
      </div>


      {/* Floating panel rendered via portal (outside the Level-2 dropdown) */}
      {renderFloatingPanel()}
    </FormGroup>
  );
};

export default FilterInput;
